// Japanese (です/ます) copy for /on-demand-ftp, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "オンデマンドFTP" and "Webアップローダー" as in the site's footer (src/i18n/locales/ja.json).
// The uploader's and FileZilla's own labels are in English, so «labels» are quoted as the app shows them.
import type { OnDemandFtpCopy } from "./copy.en";

const ja: OnDemandFtpCopy = {
  seo: {
    title: "オンデマンドFTPの仕組み | Photohawk",
    description:
      "FTPが欠かせない場合、すべてのプランで、ワークスペースのメンバーは誰でもWebアップローダーから自分専用のPhotohawk FTPサーバーを起動できます。約30秒で準備が整い、アドレスは変わることのない個人専用のもの。操作がないまま1時間たつと自動で停止します。",
  },
  hero: {
    badge: "すべてのプラン",
    eyebrow: "提供開始",
    title: "オンデマンドFTPの仕組み",
    subtitle:
      "FTPが欠かせない場合は、必要なときにWebアップローダーから自分専用のFTPサーバーを起動できます。約30秒で準備が整い、使い終わると自動で停止します。",
  },
  before: {
    title: "始める前に：WebアップローダーのほうがFTPより速い",
    body: "アップロードの標準はWebアップローダーです。すべてのプランでご利用いただけます。",
    points: [
      { id: "parallel", text: "多数の写真を同時に送信します。FileZilla（初期設定では同時に2枚、上限は10枚）のようなFTPソフトよりはるかに多い枚数です。" },
      { id: "adaptive", text: "回線の状況に合わせて調整し、回線を最大限に活用します。" },
      { id: "live", text: "プレビューを先に送るので、オリジナルの到着を待たずに数分でギャラリーを公開できます。" },
      { id: "resume", text: "接続が切れても自動で再開します。" },
    ],
    button: "Webアップローダーを開く",
    link: "新しいWebアップローダーを見る",
  },
  what: {
    heading: "必要なときに、自分専用のFTPサーバー",
    body: "すべてのプランで、ワークスペースのメンバーは誰でもWebアップローダーから自分専用のFTPサーバーを起動できます。サーバーは1人に1台で、アドレスは変わることのない個人専用のものなので、FTPソフトの設定は一度だけで済みます。使っていないときは何も動いていません。操作がないまま1時間たつと自動で停止します。",
    date: "2026年11月1日",
    closing: "常時稼働の共有FTPサーバーは2026年11月1日に終了します。現在お使いの場合は、自分専用のサーバーを起動し、FTPソフトのホストを変更してください。",
  },
  steps: {
    heading: "使い方",
    subtitle: "初回は4つのステップです。2回目からは、サーバーを起動してアップロードするだけです。",
    items: [
      { id: "open", title: "Webアップローダーを開く", body: "`upload.photohawk.com` にアクセスしてサインインするか、ギャラリーの «Upload» タブを開きます。" },
      { id: "start", title: "サーバーを起動する", body: "上部の «FTP server» をクリックし、«Start FTP server» をクリックします。約30秒で準備が整います。" },
      { id: "connect", title: "アドレスとログイン情報をFTPソフトにコピーする", body: "あなたのアドレス、ポート21、Photohawkのメールアドレスとパスワードです。アドレスは変わりません。" },
      { id: "upload", title: "アップロードする", body: "ギャラリーのフォルダに写真を追加します。アップロードがないまま1時間たつとサーバーは停止します。" },
    ],
    button: "FTPサーバーを起動",
    buttonNote: "FTPサーバーをすぐに起動できる状態でWebアップローダーが開きます。",
  },
  panel: {
    heading: "画面の見方",
    body: "必要な情報はすべてWebアップローダーの «Your FTP server» パネルにあり、各項目の横にコピーボタンがあります。",
    points: [
      { id: "address", text: "あなたのアドレス（例：`swift-heron-4821.ftp.photohawk.com`）。ずっと同じアドレスなので、設定は一度だけです。" },
      { id: "ip", text: "サーバーのIPアドレスも表示されます。IPアドレスはサーバーを起動するたびに変わるため、できるだけアドレスのほうを使ってください。" },
      { id: "countdown", text: "停止までのカウントダウンと、«Keep it running»、«Stop» のボタン。" },
    ],
    alt: "「Your FTP server」パネル。ヨーロッパ（アイルランド）で Ready と表示され、ホスト swift-heron-4821.ftp.photohawk.com、IPアドレス、ポート21、ユーザー名 alex@example.com、パッシブモード、FTP over TLS、そして Keep it running と Stop のボタンが付いたカウントダウンが表示されています。",
  },
  details: {
    heading: "接続情報",
    subtitle: "FTPソフトに次の情報を入力します。",
    rows: [
      { id: "host", label: "ホスト", value: "あなたのアドレス（例：`swift-heron-4821.ftp.photohawk.com`、または表示されているIPアドレス）" },
      { id: "port", label: "ポート", value: "21" },
      { id: "user", label: "ユーザー名", value: "Photohawkのメールアドレス" },
      { id: "password", label: "パスワード", value: "Photohawkのパスワード、またはFTPパスワード（下記参照）" },
      { id: "mode", label: "転送モード", value: "パッシブ" },
      { id: "encryption", label: "暗号化", value: "通常のFTP、またはFTPS（明示的なFTP over TLS）。FTPSではIPアドレスではなくホスト名を使ってください。" },
    ],
    switching: "これまでPhotohawkのFTPを使っていた場合も、ユーザー名、パスワード、フォルダはそのままです。ホストを新しいアドレスに変更するだけです。",
  },
  password: {
    heading: "必要なら、FTP専用のパスワードも",
    body: "FTPソフトにPhotohawkのパスワードを保存する代わりに、パネルで «Create FTP password» をクリックできます。このパスワードはあなたのFTPサーバーでのみ使え、ユーザー名はメールアドレスです。コピー、新しいパスワードの作成（古いパスワードは1分以内に使えなくなります）、削除はいつでもできます。Photohawkのパスワードも引き続き使えます。",
  },
  folders: {
    heading: "フォルダ",
    body: "フォルダ構成はこれまでと同じです。",
    path: "/Galleries/<gallery>/<collection>",
    points: [
      { id: "single", text: "コレクションが1つだけのギャラリーは、単に `/Galleries/<gallery>` になります。" },
      { id: "workspaces", text: "複数のワークスペースにアップロードする場合は、ワークスペースごとにフォルダがあり、その中にギャラリーが入っています。" },
      { id: "create", text: "先にPhotohawkでギャラリーとコレクションを作成してください。写真はWebアップローダーと同じように、すぐにギャラリーに表示されます。" },
    ],
  },
  off: {
    heading: "自動で停止します",
    body: "操作がないまま1時間たつとサーバーは自動で停止するので、止め忘れの心配はありません。パネルにはカウントダウンが表示されます。",
    points: [
      { id: "keep", text: "«Keep it running» を押すと、何もアップロードしなくてもカウントダウンがリセットされます。" },
      { id: "stop", text: "«Stop» を押すと、作業が終わったときにすぐ停止します。" },
      { id: "caps", text: "安全のため、アップロードがない状態が12時間続いた場合も停止し、最長でも24時間で停止します。" },
      { id: "again", text: "同じアドレスとログイン情報で、いつでも再起動できます。" },
    ],
  },
  regions: {
    heading: "リージョン",
    body: "サーバーはヨーロッパ（アイルランド）、米国東部（バージニア北部）、アジアパシフィック（マレーシア）で起動できます。最も近いリージョンが自動で選ばれ、起動前に変更することもできます。",
  },
  filezilla: {
    heading: "例：FileZillaの設定",
    subtitle: "他のFTPソフトも同じ設定を使います。項目名が少し違うことがあります。",
    steps: [
      { id: "site", text: "«File → Site Manager» を開き、«New site» をクリックします。" },
      { id: "protocol", text: "プロトコル：«FTP – File Transfer Protocol»。" },
      { id: "host", text: "ホスト：あなたのアドレス（例：`swift-heron-4821.ftp.photohawk.com`）。ポート：`21`。" },
      { id: "tls", text: "暗号化：«Use explicit FTP over TLS if available»。" },
      { id: "login", text: "ログオンの種類：«Normal»。ユーザー：Photohawkのメールアドレス。パスワード：FTPパスワードまたはPhotohawkのパスワード。" },
      { id: "passive", text: "«Transfer Settings» タブで «Passive» を選びます。«Connect» をクリックして、`Galleries` を開きます。" },
    ],
    tip: "大きなフォルダを送るときは？ FileZillaは初期設定では同時に2ファイル、上限は10ファイルまでしか送れません。同時転送数の設定は10まで上げられます（«Settings → Transfers»）。あるいは、はるかに多くの写真を同時に送れるWebアップローダーをお使いください。",
  },
  trouble: {
    heading: "うまくいかないときは",
    items: [
      { id: "connect", title: "接続できない", body: "パネルに «Ready» と表示されているか確認してください。ホスト名が作成された直後の場合は、1分ほどIPアドレスを使ってください。" },
      { id: "listing", title: "接続はできたが、フォルダ一覧が表示されない", body: "パッシブモードをオンにしてください。" },
      { id: "logins", title: "「Too many failed logins」", body: "15分待ってから、ユーザー名（メールアドレス）とパスワードを確認してください。" },
    ],
  },
  faq: {
    heading: "よくある質問",
    items: [
      {
        q: "サーバーが停止したのはなぜですか？",
        a: "使っていないときに動き続けないよう、操作がないまま1時間たつと停止します。いつでも再起動でき、アドレスとログイン情報は変わりません。アップロードせずに動かし続けたい場合は、«Keep it running» をクリックしてください。また、アップロードがない状態が12時間続いた場合も停止し、最長でも24時間で停止します。",
      },
      {
        q: "2人が同時にサーバーを使えますか？",
        a: "はい。ワークスペースのメンバーはそれぞれ自分のアドレスで、1人1台ずつサーバーを起動できます。あなたのサーバーにログインできるのはあなただけです。",
      },
      {
        q: "安全ですか？",
        a: "はい。ホスト名でFTPS（FTP over TLS）を使えば、信頼された証明書で接続が暗号化されます。あなたのサーバーで使えるのはあなたのログイン情報だけで、ログインの失敗が続くと、サーバーはその接続を15分間ブロックします。",
      },
      {
        q: "追加料金はかかりますか？",
        a: "かかりません。自分専用のFTPサーバーはすべてのプランに含まれています。",
      },
      {
        q: "すべてのプランで使えますか？",
        a: "はい、Liteでも使えます。普段のアップロードにはWebアップローダーやPhotohawk Studioのほうが速く、FTPが欠かせないときは自分専用のFTPサーバーを使えます。",
      },
      {
        q: "これまでの共有FTPはどうなりますか？",
        a: "常時稼働の共有FTPサーバーは2026年11月1日に終了し、それまではこれまでどおり使えます。引き続きFTPを使う場合は、自分専用のサーバーを起動し、FTPソフトのホストを新しいアドレスに変更してください。ユーザー名、パスワード、フォルダは変わりません。",
      },
    ],
  },
  cta: {
    heading: "準備はいつでもできています",
    body: "普段のアップロードにはWebアップローダーをお使いください。FTPが欠かせないときは、ワンクリックで自分専用のサーバーを使えます。",
    primary: "Webアップローダーを開く",
    secondary: "FTPサーバーを起動",
  },
};

export default ja;
